import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../stores';
import QRLoginModal from '../../components/auth/QRLoginModal';

/**
 * 登录页面
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState<'password' | 'code'>('password');
  const [formData, setFormData] = useState({
    contact: '',
    password: '',
    verificationCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [showQRLogin, setShowQRLogin] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const { login, loginWithCode, sendVerificationCode, loginState, verificationState, setTokens, getCurrentUser } = useStore();

  // 检查是否因会话过期跳转
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session_expired') {
      setSessionExpired(true);
      // 清除 URL 参数
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 登录成功后跳转
  useEffect(() => {
    if (loginState === 'success') {
      navigate('/chat');
    }
  }, [loginState, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // 验证输入
    if (!formData.contact) {
      setError('请输入邮箱或手机号');
      setIsLoading(false);
      return;
    }

    try {
      if (loginType === 'password') {
        if (!formData.password) {
          setError('请输入密码');
          setIsLoading(false);
          return;
        }
        await login({
          contact: formData.contact,
          password: formData.password
        });
      } else {
        if (!formData.verificationCode) {
          setError('请输入验证码');
          setIsLoading(false);
          return;
        }
        await loginWithCode({
          contact: formData.contact,
          verificationCode: formData.verificationCode
        });
      }
    } catch (err: any) {
      setError(err.message || '登录失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (countdown > 0) return;
    
    if (!formData.contact) {
      setError('请输入邮箱或手机号');
      return;
    }

    // 验证格式
    const isEmail = formData.contact.includes('@');
    if (isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact)) {
      setError('请输入正确的邮箱地址');
      return;
    }

    if (!isEmail && !/^1[3-9]\d{9}$/.test(formData.contact)) {
      setError('请输入正确的手机号');
      return;
    }

    try {
      setError('');
      await sendVerificationCode({
        contact: formData.contact,
        type: 'login'
      });
      setCodeSent(true);
      setCountdown(60);
    } catch (err: any) {
      setError(err.message || '发送验证码失败');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // 处理扫码登录成功
  const handleQRLoginSuccess = async (tokens: { accessToken: string; refreshToken: string }) => {
    try {
      // 1. 设置token
      setTokens(tokens.accessToken, tokens.refreshToken);
      
      // 2. 加载用户信息
      await getCurrentUser();
      
      // 3. 关闭弹窗并跳转
      setShowQRLogin(false);
      navigate('/chat');
    } catch (error) {
      console.error('Failed to load user info after QR login:', error);
      // 即使加载用户信息失败，也允许跳转（token已设置）
      setShowQRLogin(false);
      navigate('/chat');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* 标题 */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img 
              src="/images/logo.jpg" 
              alt="AI数理化助手" 
              className="w-12 h-12 mr-3 rounded-lg shadow-lg object-cover"
            />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              AI数理化助手
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            登录开始您的学习之旅
          </p>
        </div>

        {/* 登录表单 */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {sessionExpired && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
                您的登录会话已过期，请重新登录
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {codeSent && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                验证码已发送，请查收
              </div>
            )}

            {/* 邮箱或手机号输入 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                请输入您注册的邮箱或手机号
              </label>
              <input
                type="text"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className="input w-full"
                placeholder="邮箱或手机号"
                required
              />
            </div>

            {/* 密码或验证码输入 */}
            {loginType === 'password' ? (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  密码
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="input w-full"
                  placeholder="请输入密码"
                  value={formData.password}
                  onChange={handleChange}
                />
                <div className="mt-2 flex justify-between text-sm">
                  <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500">
                    忘记密码？
                  </Link>
                  <button
                    type="button"
                    onClick={() => setLoginType('code')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    验证码登录
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  验证码
                </label>
                <div className="flex space-x-2">
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    required
                    className="input flex-1"
                    placeholder="请输入验证码"
                    value={formData.verificationCode}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={countdown > 0 || verificationState === 'loading'}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium min-w-[100px]"
                  >
                    {countdown > 0 ? `${countdown}s` : verificationState === 'loading' ? '发送中...' : '发送验证码'}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => setLoginType('password')}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    返回密码登录
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || loginState === 'loading'}
              className="btn btn-primary w-full"
            >
              {isLoading || loginState === 'loading' ? '登录中...' : '登录'}
            </button>
          </form>

          {/* 微信扫码登录 */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">或</span>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowQRLogin(true)}
                className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                微信扫码登录
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              还没有账号？{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
                立即注册
              </Link>
            </p>
          </div>
        </div>

        {/* 功能介绍 */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <p>支持数学、物理、化学问题解答</p>
          <p>智能图像识别 • 实时对话 • 个性化辅导</p>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            版权所有 深圳欣易达科技 粤ICP备 2026025630号
          </p>
        </div>

        {/* 扫码登录弹窗 */}
        <QRLoginModal
          isOpen={showQRLogin}
          onClose={() => setShowQRLogin(false)}
          onSuccess={handleQRLoginSuccess}
        />
      </div>
    </div>
  );
};

export default LoginPage;